import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const VERSION_URL = '/version.json';
const TOAST_ID = 'app-version-update';
const DEFAULT_INTERVAL_MINUTES = 3;

const renderCloseButton = ({ closeToast }) => React.createElement(
	'button',
	{
		type: 'button',
		onClick: (e) => {
			e.stopPropagation();
			closeToast(e);
		},
		'aria-label': 'close',
		style: {
			color: '#000',
			background: 'transparent',
			border: 'none',
			cursor: 'pointer',
			padding: 12,
			marginTop: -8,
			marginRight: -8,
			fontSize: 20,
			fontWeight: 700,
			lineHeight: 1,
			alignSelf: 'flex-start',
		},
	},
	'\u2715'
);

const resolveIntervalMs = () => {
	const minutes = parseFloat(process.env.REACT_APP_VERSION_CHECK_INTERVAL) || DEFAULT_INTERVAL_MINUTES;
	return minutes * 60 * 1000;
};

const CHECK_INTERVAL_MS = resolveIntervalMs();

/**
 * Read the running bundle hash from the <script src="/static/js/main.<hash>.js"> tag
 * injected by CRA into index.html. Returns null in development (no hashed bundle).
 */
const readCurrentVersion = () => {
	const scripts = document.getElementsByTagName('script');
	for (let i = 0; i < scripts.length; i++) {
		const src = scripts[i].getAttribute('src') || '';
		const match = src.match(/\/static\/js\/main\.([^./]+)\.js/);
		if (match) return match[1];
	}
	return null;
};

const fetchRemoteVersion = async () => {
	const res = await fetch(`${VERSION_URL}?_=${Date.now()}`, {
		cache: 'no-store',
		headers: { 'Cache-Control': 'no-cache' },
	});
	if (!res.ok) return null;
	const data = await res.json();
	return typeof data?.version === 'string' ? data.version : null;
};

/**
 * Periodically compare the bundle hash baked into index.html with the value
 * written to /version.json by the post-build script. When they differ, prompt
 * the user to reload so the browser fetches the new index.html and bundle.
 */
const useVersionCheck = () => {
	const currentRef = useRef(readCurrentVersion());
	const notifiedRef = useRef(false);

	useEffect(() => {
		if (!currentRef.current) return undefined; // run only in builds

		let cancelled = false;
		let intervalId = null;

		const stopPolling = () => {
			if (intervalId !== null) {
				clearInterval(intervalId);
				intervalId = null;
			}
		};

		const startPolling = () => {
			stopPolling();
			if (!cancelled) {
				intervalId = setInterval(check, CHECK_INTERVAL_MS);
			}
		};

		const handleToastClose = () => {
			notifiedRef.current = false;
			startPolling();
		};

		const check = async () => {
			if (cancelled || notifiedRef.current) return;
			try {
				const remote = await fetchRemoteVersion();
				if (cancelled || notifiedRef.current) return;
				if (remote && remote !== currentRef.current) {
					notifiedRef.current = true;
					stopPolling();
					toast.warning(
						'Dostępna jest nowa wersja aplikacji!\nKliknij na ten komunikat, aby odświeżyć stronę i załadować aktualną wersję.\nUWAGA: wszystkie niezapisane zmiany zostaną utracone!\nPrzed odświeżeniem upewnij się, że Twoja praca została zapisana.',
						{
							toastId: TOAST_ID,
							autoClose: false,
							closeOnClick: false,
							draggable: false,
							onClick: () => window.location.reload(),
							onClose: handleToastClose,
							closeButton: renderCloseButton,
							style: {
								width: 730,
								maxWidth: '90vw',
								left: '50%',
								marginLeft: '-365px',
								color: '#000',
								boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
								whiteSpace: 'pre-line',
							},
						}
					);
				}
			} catch (_) {
				// Offline / network error — retry on next tick.
			}
		};

		check();
		startPolling();

		return () => {
			cancelled = true;
			stopPolling();
		};
	}, []);
};

/**
 * Renderless component that activates the version polling. Mount once, near the
 * top of the tree, alongside the global ToastContainer.
 */
export const VersionWatcher = () => {
	useVersionCheck();
	return null;
};

export default useVersionCheck;
